import { create } from 'zustand'
import { marketApi } from '../services'

let marketRequestId = 0

const useMarketStore = create((set, get) => ({
  // Состояние
  skins: [], // Список скинов в магазине
  availableFilters: {}, // Доступные фильтры из API для текущей игры (exterior, rarity, type, stattrak, souvenir)
  games: [], // Список доступных игр из API [{ slug, name }]
  filtersByGame: {}, // Фильтры по каждой игре { cs2: {...}, dota2: {...}, rust: {...} }
  currentGame: 'cs2', // Текущая выбранная игра
  isLoading: false,
  
  // Текущие фильтры пользователя
  filters: {
    stattrak: 'any',
    souvenir: 'any',
    exterior: 'any',
    rarity: 'any',
    type: 'any',
    price_start: 'any',
    price_finish: 'any',
    search_string: 'any',
  },
  
  // Пагинация
  displayCount: 30, // Сколько скинов показываем
  totalLoaded: 0, // Сколько всего загружено

  invalidate: () => {
    marketRequestId += 1
    set({ skins: [], availableFilters: {}, totalLoaded: 0, displayCount: 30, isLoading: false })
  },
  
  /**
   * Загрузка начальных данных магазина (GET /api/market/?game=)
   */
  loadInitialData: async (game = null) => {
    const requestId = ++marketRequestId
    const currentGame = game || get().currentGame || 'cs2'
    set({ isLoading: true })
    try {
      const { found, filter, games, filtersByGame } = await marketApi.getInitialData(currentGame)
      if (requestId !== marketRequestId) return null
      
      set({ 
        skins: found || [],
        availableFilters: filter || {},
        games: games || [],
        filtersByGame: filtersByGame || {},
        currentGame,
        totalLoaded: found?.length || 0,
        displayCount: 30,
      })
      
      return { found, filter, games, filtersByGame }
    } catch (error) {
      if (requestId === marketRequestId) {
        console.error('❌ Ошибка загрузки данных магазина:', error)
        set({ skins: [], availableFilters: {} })
        throw error
      }
      return null
    } finally {
      if (requestId === marketRequestId) set({ isLoading: false })
    }
  },
  
  /**
   * Применение фильтров (POST /api/market/)
   */
  applyFilters: async () => {
    const requestId = ++marketRequestId
    set({ isLoading: true })
    try {
      const currentFilters = get().filters
      const currentGame = get().currentGame
      // Добавляем game в тело запроса
      const requestBody = { game: currentGame, ...currentFilters }
      const { found, games, filtersByGame } = await marketApi.applyFilters(requestBody)
      if (requestId !== marketRequestId) return null
      
      set({ 
        skins: found || [],
        games: games || get().games,
        filtersByGame: filtersByGame || get().filtersByGame,
        totalLoaded: found?.length || 0,
        displayCount: 30, // Сбрасываем к начальному значению
      })
      
      return found
    } catch (error) {
      if (requestId === marketRequestId) {
        console.error('❌ Ошибка применения фильтров:', error)
        set({ skins: [] })
        throw error
      }
      return null
    } finally {
      if (requestId === marketRequestId) set({ isLoading: false })
    }
  },
  
  /**
   * Обновление конкретного фильтра
   */
  updateFilter: (filterName, value) => {
    set((state) => ({
      filters: {
        ...state.filters,
        [filterName]: value,
      },
    }))
  },
  
  /**
   * Сброс всех фильтров
   */
  resetFilters: () => {
    set({
      filters: {
        stattrak: 'any',
        souvenir: 'any',
        exterior: 'any',
        rarity: 'any',
        type: 'any',
        price_start: 'any',
        price_finish: 'any',
        search_string: 'any',
      },
    })
  },
  
  /**
   * Переключение игры: мгновенно обновляет фильтры из filtersByGame, сбрасывает значения,
   * параллельно запрашивает список товаров новой игры
   */
  switchGame: async (gameSlug) => {
    const requestId = ++marketRequestId
    const { filtersByGame } = get()
    
    // Мгновенно обновляем фильтры из кэшированных данных, без ожидания сети
    const gameFilters = filtersByGame[gameSlug] || {}
    
    // Сбрасываем фильтры, которых нет у новой игры
    const resetValues = {
      stattrak: 'any',
      souvenir: 'any',
      exterior: 'any',
      rarity: 'any',
      type: 'any',
      price_start: 'any',
      price_finish: 'any',
      search_string: 'any',
    }
    
    set({
      currentGame: gameSlug,
      availableFilters: gameFilters,
      filters: resetValues,
    })
    
    // Запрашиваем список товаров для новой игры
    set({ isLoading: true })
    try {
      const { found, filter, games, filtersByGame: newFiltersByGame } = await marketApi.getInitialData(gameSlug)
      if (requestId !== marketRequestId) return null
      
      set({ 
        skins: found || [],
        availableFilters: filter || gameFilters,
        games: games || get().games,
        filtersByGame: newFiltersByGame || get().filtersByGame,
        totalLoaded: found?.length || 0,
        displayCount: 30,
      })
      
      return found
    } catch (error) {
      if (requestId === marketRequestId) {
        console.error('❌ Ошибка переключения игры в магазине:', error)
        set({ skins: [] })
        throw error
      }
      return null
    } finally {
      if (requestId === marketRequestId) set({ isLoading: false })
    }
  },
  
  /**
   * Показать еще 30 скинов
   */
  loadMore: () => {
    set((state) => ({
      displayCount: state.displayCount + 30,
    }))
  },
  
  /**
   * Покупка скина
   */
  buySkin: async (skinName) => {
    try {
      const currentGame = get().currentGame
      const { purchasedSkin, user } = await marketApi.buySkin(skinName, currentGame)
      
      // Баланс обновится автоматически через processUnifiedResponse в api.js
      return { purchasedSkin, user }
    } catch (error) {
      console.error('❌ Ошибка покупки скина:', error)
      throw error
    }
  },
}))

export default useMarketStore
